import { getCurrencySymbol } from "@/pages/deals/helpers";
// Intl-backed, unlike the currency-list getCurrencySymbol above: this is the
// sign formatCurrency itself prints, which is what the override replaces.
import { getCurrencySymbol as getIntlCurrencySymbol } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import { Commercials } from "@/pages/deals/types";
import {
  useAccountSettingByKey,
  useCurrencyRates
} from "@/entities/settings/hooks";
import {
  currencyCodeFromSetting,
  distinguishCurrencySign,
  isCurrencyConversionNeeded
} from "@/utils/numbers";

export const DEFAULT_CURRENCY_CODE = "USD";

export const useFinancials = (commercial: Commercials | null) => {
  const accountCurrencySetting =
    useAccountSettingByKey("account_currency").data;
  // Named for what it is, matching useCurrencyConversion: the resolved truth,
  // null until the setting lands and never a stand-in. `AccountSetting.value`
  // is `string | boolean | ISOStringFormat | null`, so this narrows rather
  // than casting — an `as string` here is what let a `.toUpperCase()` through
  // on a value the type permits to be null or false.
  const resolvedAccountCurrency = currencyCodeFromSetting(
    accountCurrencySetting?.value
  );
  // The defaulted convenience value, for the rate lookup and symbol only —
  // never for a currency comparison.
  const accountCurrency = resolvedAccountCurrency ?? DEFAULT_CURRENCY_CODE;
  const contractCurrencyCode = commercial?.currency || DEFAULT_CURRENCY_CODE;
  // Falls back to the code: getCurrencySymbol answers undefined for a currency
  // its package does not carry (FJD, XCD), and consumers interpolate this into
  // the displayed value, so the figure would read "undefined1,234".
  const accountCurrencySymbol =
    getCurrencySymbol(accountCurrency) || accountCurrency;
  // Every consumer renders the contract's figure with the account's converted
  // one beside it, so a contract currency whose symbol is the account's own
  // glyph — `$` for CAD on a USD account — leaves the pair reading as one
  // currency.
  //
  // The account code is read straight off the setting rather than from the
  // DEFAULT_CURRENCY_CODE fallback above: that fallback exists so the rate
  // lookup has a base, and comparing against a guessed currency would qualify
  // a contract in the account's own currency until the setting resolved.
  //
  // Two values, because the consumers render through two different resolvers
  // and the sign compared has to be the sign that consumer actually prints.
  // For the ones that interpolate it into a template literal beside an
  // accountCurrencySymbol from the same getCurrencySymbol:
  const contractCurrencySymbol =
    distinguishCurrencySign({
      sign: getCurrencySymbol(contractCurrencyCode),
      // The raw currency, like showConversion below: a record with none
      // recorded is unknown, not USD, and comparing the defaulted code
      // qualified it to the literal "USD" on any $-glyph account.
      currencyCode: commercial?.currency,
      accountSign: accountCurrencySymbol,
      accountCurrencyCode: resolvedAccountCurrency
      // Falls back to the code itself, not to getCurrencySymbol again: that
      // answers undefined for a currency its package does not carry, and every
      // consumer interpolates this into a template literal, so the figure
      // would read "undefined5,000". Naming the code is what the Intl-backed
      // resolver does for the same case.
    }) ?? contractCurrencyCode;
  // And for the ones that hand it to formatCurrency as a sign override. No
  // `??` fallback: formatCurrency ignores a falsy override and prints its own
  // sign, which is the right answer when there is nothing to tell apart.
  const contractCurrencySignOverride = distinguishCurrencySign({
    sign: getIntlCurrencySymbol(contractCurrencyCode),
    currencyCode: commercial?.currency,
    accountSign: accountCurrencySymbol,
    accountCurrencyCode: resolvedAccountCurrency
  });
  // Gated on the resolved setting, not the DEFAULT_CURRENCY_CODE fallback
  // above: while it loads the account currency is unknown rather than USD, and
  // treating it as USD showed a conversion that vanished once a record in the
  // account's own currency resolved.
  // The raw record currency, not contractCurrencyCode's DEFAULT-defaulted
  // value. Comparing the defaulted one still suppressed the USD-to-USD
  // conversion this was originally changed to suppress, but only because the
  // account happened to be USD too — on a CAD account it offered a USD-to-CAD
  // conversion for a record whose currency is simply unknown, which the other
  // two sites correctly declined. The guard rejects the unresolved value on
  // its own, so both cases fall out of one rule.
  const showConversion = isCurrencyConversionNeeded(
    resolvedAccountCurrency,
    commercial?.currency
  );
  const rates = useCurrencyRates(
    commercial?.currency || DEFAULT_CURRENCY_CODE
  )?.data;
  const rate = rates?.rates[accountCurrency as string];

  return {
    contractCurrencySymbol,
    contractCurrencySignOverride,
    accountCurrencySymbol,
    showConversion,
    rate
  };
};
